/**
 * Reveals the PDP action zone on MOBILE only, once the price has cleared it.
 *
 * The problem. On desktop the band is the bottom of a pane that sits beside the
 * gallery, so having it there from the first paint is the whole point — that is
 * the defect this ticket exists to fix. On mobile the layout is stacked: the
 * gallery is full-bleed and the pane starts below it, so a band pinned from the
 * first paint sits over the product image and covers the title before the
 * shopper has seen the price. Measured on an iPhone at 390x844, the band was
 * overlapping "Chino bleu marine" on landing.
 *
 * The rule. Show the band once the PRICE has scrolled clear of where the band
 * sits — i.e. when the price's bottom edge is at or above (viewport bottom −
 * band height). That is exactly the moment the shopper can see the price and the
 * band at the same time, and it is the state the operator marked as correct.
 *
 * Keying off the price rather than a fixed scroll distance matters: the gallery
 * is a different height on every product, so any pixel threshold would fire at
 * the right place on one PDP and the wrong place on the next.
 *
 * Mechanism is a class, not an inline style: the transform and its transition
 * live in CSS, so the band still animates if this file is slow, and a browser
 * honouring prefers-reduced-motion can drop the animation without touching the
 * behaviour. Before this runs the band is hidden by the same CSS, so there is no
 * flash of a band that then slides away.
 *
 * Desktop is deliberately untouched. The breakpoint is read once from a media
 * query matching the stylesheet's own 749px boundary; above it the class is set
 * permanently and no scroll work happens at all.
 */
(() => {
  const REVEALED = 'product__action-zone--revealed';
  const MOBILE = window.matchMedia('(max-width: 749px)');

  const init = () => {
    const zone = document.querySelector('.product__action-zone');
    if (!zone) return;

    const pane = zone.closest('.product__info-container');
    const price = pane && pane.querySelector('[id^="price-"]');

    // No price block on this template: reveal and stop. A band that never appears
    // is a worse failure than one that appears too early.
    if (!price) {
      zone.classList.add(REVEALED);
      return;
    }

    let frame = null;

    const update = () => {
      frame = null;

      if (!MOBILE.matches) {
        zone.classList.add(REVEALED);
        document.documentElement.style.removeProperty('--pdp-action-zone-h');
        return;
      }

      const priceBottom = price.getBoundingClientRect().bottom;
      const bandHeight = zone.offsetHeight;
      const shouldShow = priceBottom <= window.innerHeight - bandHeight;

      zone.classList.toggle(REVEALED, shouldShow);

      // The band is `position: fixed` on mobile, so it no longer occupies space in
      // the pane. Publish its height and let CSS give that space back as padding —
      // otherwise the last rows of the pane sit permanently underneath it.
      document.documentElement.style.setProperty('--pdp-action-zone-h', bandHeight + 'px');
    };

    // Coalesce to one measurement per frame: scroll fires far more often than the
    // layout can meaningfully change, and getBoundingClientRect forces layout.
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    MOBILE.addEventListener('change', schedule);

    // The band's own height changes when the variant picker rewraps or the size
    // guide loads, and the threshold depends on it — so re-measure on that too.
    if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(zone);

    update();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
